import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import CustomerLogotypes from "../../../../components/CustomerLogotypes";
import { toast } from "react-toastify";

import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import Icon from "../../../../components/Icon";
import InputColor from "../../../../components/InputColor";
import SelectProduct from "../../../../components/selects/SelectProduct";
import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import { getBase64 } from "../../../../helpers/conversions";
import { getProductGalleryImageUrls } from "../subproductImage";
import {
    CORES_TINTA,
    FAMILIAS,
    MATERIAIS,
    SEM_BRANCO,
    SEM_TINTA,
    TECNICA_PADRAO,
    TECNICAS,
    corImpossivel,
    materialPelaDescricao,
    tecnicaParaMaterial
} from "./opcoes";
import { arteChapada, margemRecortada, tirarFundoBranco } from "./fundoBranco";
import { cantosDaCaixa, convexo, matrizCss } from "./perspectiva";
import "./styles.scss";

const PASSOS = [
    { id: 'posicao', rotulo: 'Logo e posição' },
    { id: 'gravacao', rotulo: 'Gravação e cor' }
];

/**
 * Espera antes de compor. Cobre o arrasto da marcação (que muda o estado a
 * cada pixel) e a indecisão entre dois cards, sem deixar a escolha parecer
 * ignorada.
 */
const ATRASO = 320;

const arredondar = (valor) => Math.round(valor * 1e4) / 1e4;

// Área mínima da marcação (fração do lado da imagem) para evitar cliques acidentais.
const MIN_AREA_SIDE = 0.03;

// Acima desta proporção altura/largura a marcação nasce deitada (-90°): quem
// arrasta uma faixa estreita e alta está marcando gravação vertical (lateral
// de caneca, garrafa, régua).
const VERTICAL_RATIO = 2;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const LAYOUT_EMPILHADO = '(max-width: 900px)';

/**
 * O tamanho em que a foto cabe inteira na caixa do palco. A marcação é medida
 * no retângulo do palco e o backend a aplica na foto: se a foto passa da caixa
 * (a faixa de miniaturas do passo 1 come altura que um max-height em dvh não
 * enxerga), o palco fica mais baixo que ela e a gravação sai deslocada.
 * Empilhado (mobile) a caixa cresce com o conteúdo e o CSS já limita a foto.
 */
const useFittedImageSize = (box, src) => {
    const [natural, setNatural] = useState(null);
    const [space, setSpace] = useState(null);

    useEffect(() => {
        setNatural(null);
        if (!src) return undefined;

        let alive = true;
        const img = new window.Image();
        img.onload = () => {
            if (alive) setNatural({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = src;

        return () => { alive = false; };
    }, [src]);

    useEffect(() => {
        if (!box) return undefined;

        const measure = () => {
            if (window.matchMedia(LAYOUT_EMPILHADO).matches) {
                setSpace(null);
                return;
            }

            const style = window.getComputedStyle(box);
            const width = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
            const height = box.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);

            setSpace((atual) => (atual?.width === width && atual?.height === height ? atual : { width, height }));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(box);

        return () => observer.disconnect();
    }, [box]);

    if (!natural?.width || !natural?.height || !space || space.width <= 0 || space.height <= 0) return null;

    const scale = Math.min(1, space.width / natural.width, space.height / natural.height);

    return {
        width: Math.floor(natural.width * scale),
        height: Math.floor(natural.height * scale),
        maxHeight: 'none'
    };
};

// A marcação vive em coordenadas normalizadas (contrato da API), mas giro e
// redimensionamento com giro só fecham em pixels — o palco não é quadrado, e
// um vetor girado em coordenadas 0..1 sai torto.
const areaToPx = (area, rect) => ({
    cx: (area.x + area.width / 2) * rect.width,
    cy: (area.y + area.height / 2) * rect.height,
    w: area.width * rect.width,
    h: area.height * rect.height
});

const boxToArea = (box, rect) => {
    const w = Math.max(MIN_AREA_SIDE * rect.width, Math.min(box.w, rect.width));
    const h = Math.max(MIN_AREA_SIDE * rect.height, Math.min(box.h, rect.height));
    const cx = Math.min(rect.width - w / 2, Math.max(w / 2, box.cx));
    const cy = Math.min(rect.height - h / 2, Math.max(h / 2, box.cy));
    return {
        x: (cx - w / 2) / rect.width,
        y: (cy - h / 2) / rect.height,
        width: w / rect.width,
        height: h / rect.height
    };
};

// Troca os lados em torno do mesmo centro: 20x50 vira 50x20 e, girada -90°,
// ocupa exatamente a pegada que foi arrastada.
const swapSides = (area, rect) => {
    const box = areaToPx(area, rect);
    return boxToArea({ cx: box.cx, cy: box.cy, w: box.h, h: box.w }, rect);
};

/**
 * As APIs de imagem (OpenAI/Gemini) não aceitam SVG — o browser rasteriza de
 * graça via canvas. Como SVG é vetor, renderiza direto no tamanho alvo (lado
 * maior = 1024px) para o logo chegar nítido.
 */
/**
 * Cores dominantes do logo, para oferecer como cor de gravação. Roda no
 * browser porque a arte já está aqui — mandar para o backend seria uma volta
 * inteira para uma sugestão de tela.
 *
 * Duas no máximo, e a segunda só entra se for expressiva e distinta: logo de
 * arte fotográfica tem centenas de tons, e listar todos não ajuda ninguém a
 * escolher. Arte sem transparência tem o branco tratado como fundo, a mesma
 * regra que a composição usa no backend.
 */
const LOGO_COLOR_SAMPLE = 128;
const LOGO_COLOR_MIN_SHARE = 0.15;
const LOGO_COLOR_MIN_DISTANCE = 90;

const colorDistance = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);

const extractLogoColors = (dataUrl) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onerror = () => resolve([]);
        img.onload = () => {
            const scale = Math.min(1, LOGO_COLOR_SAMPLE / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));

            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(img, 0, 0, canvas.width, canvas.height);

            let pixels;
            try {
                pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
            } catch (e) {
                resolve([]);
                return;
            }

            let opaque = 0;
            for (let i = 3; i < pixels.length; i += 4) {
                if (pixels[i] > 200) opaque++;
            }
            const solid = opaque > (pixels.length / 4) * 0.98;

            const buckets = new Map();
            let ink = 0;

            for (let i = 0; i < pixels.length; i += 4) {
                const [r, g, b, a] = [pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]];
                if (a < 128) continue;
                if (solid && 0.299 * r + 0.587 * g + 0.114 * b > 240) continue;

                const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
                const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
                bucket.r += r;
                bucket.g += g;
                bucket.b += b;
                bucket.n++;
                buckets.set(key, bucket);
                ink++;
            }

            if (!ink) {
                resolve([]);
                return;
            }

            const ranked = [...buckets.values()]
                .sort((a, b) => b.n - a.n)
                .map((bucket) => ({
                    n: bucket.n,
                    rgb: [Math.round(bucket.r / bucket.n), Math.round(bucket.g / bucket.n), Math.round(bucket.b / bucket.n)]
                }));

            // Branco e preto já têm chip fixo: sugerir de novo só ocupa
            // espaço e confunde quem escolhe.
            const fixed = [[255, 255, 255], [26, 26, 26]];
            const picked = [];

            for (const candidate of ranked) {
                if (picked.length === 2) break;
                if (picked.length && candidate.n / ink < LOGO_COLOR_MIN_SHARE) break;
                if (fixed.some((preset) => colorDistance(preset, candidate.rgb) <= LOGO_COLOR_MIN_DISTANCE)) continue;
                if (picked.some((chosen) => colorDistance(chosen.rgb, candidate.rgb) <= LOGO_COLOR_MIN_DISTANCE)) continue;

                picked.push(candidate);
            }

            resolve(
                picked.map(
                    ({ rgb }) =>
                        `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
                )
            );
        };

        img.src = dataUrl;
    });

/**
 * O logo como a gravação vai sair, sem o fundo branco e sem a margem que veio
 * no arquivo: o mesmo algoritmo da vitrine e do servidor
 * (ProductMock/WhiteBackground e ArtMargin). Só a prévia usa — para o backend
 * segue o arquivo original, e é ele quem separa e corta ao compor.
 */
const LOGO_PREVIEW_SIDE = 1024;

const removeLogoBackground = (dataUrl) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onerror = () => resolve(dataUrl);
        img.onload = () => {
            const scale = Math.min(1, LOGO_PREVIEW_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
            canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));

            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(img, 0, 0, canvas.width, canvas.height);

            const map = context.getImageData(0, 0, canvas.width, canvas.height);
            const opaque = arteChapada(map.data);

            if (opaque) tirarFundoBranco(map.data, canvas.width, canvas.height);

            const box = margemRecortada(map.data, canvas.width, canvas.height);

            if (!opaque && !box) {
                resolve(dataUrl);
                return;
            }

            if (box) {
                canvas.width = box.largura;
                canvas.height = box.altura;
            }

            context.putImageData(map, box ? -box.x : 0, box ? -box.y : 0);
            resolve(canvas.toDataURL('image/png'));
        };

        img.src = dataUrl;
    });

const rasterizeSvgToPng = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new window.Image();
            img.onload = () => {
                const targetSide = 1024;
                const w = img.naturalWidth || targetSide;
                const h = img.naturalHeight || targetSide;
                const scale = targetSide / Math.max(w, h);
                const canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(w * scale));
                canvas.height = Math.max(1, Math.round(h * scale));
                canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const IconePerspectiva = () => (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden>
        <path d="M7 5h10l4 14H3z" />
    </svg>
);

const rectFromPoints = (a, b) => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y)
});

/**
 * Fluxo de geração da amostra virtual com IA: o vendedor marca a área de
 * gravação sobre a foto do produto, anexa o logo e descreve o estilo; o
 * backend chama o modelo de imagem e devolve o mockup pronto. A imagem
 * aprovada segue o mesmo caminho do upload manual (onApply → base64).
 */
export const ModalGenerateMockup = ({
    show,
    close,
    baseImageUrl,
    imageOptions,
    productName,
    productDescription = null,
    quoteId,
    itemId,
    productId,
    subproductId,
    // Dono dos logotipos guardados (BDM-1130): quem já mandou a arte uma vez
    // não precisa mandar de novo.
    companyId = null,
    customerId = null,
    onApply,
    // Aberto pelo menu Ferramentas, sem orçamento por trás: a foto do produto
    // passa a ser escolhida num passo antes do resto.
    chooseSource = false
}) => {
    const [step, setStep] = useState(chooseSource ? 'source' : 'posicao');

    const [sourceProductId, setSourceProductId] = useState(null);
    const [sourceProducts, setSourceProducts] = useState([]);
    const [sourceDetail, setSourceDetail] = useState(null);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [uploadedName, setUploadedName] = useState('');

    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [markedArea, setMarkedArea] = useState(null);
    const [draftArea, setDraftArea] = useState(null);
    const dragStartRef = useRef(null);
    const stageRef = useRef(null);
    // Tamanho do palco em px: a prévia da perspectiva é um matrix3d, e ele só
    // fecha em pixels.
    const [stageEl, setStageEl] = useState(null);
    const [palco, setPalco] = useState(null);
    const ligarPalco = useCallback((el) => {
        stageRef.current = el;
        setStageEl(el);
    }, []);
    const [stageBox, setStageBox] = useState(null);

    const [logoBase64, setLogoBase64] = useState(null);
    const [logoFileName, setLogoFileName] = useState('');
    const [logotypeId, setLogotypeId] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    const [technique, setTechnique] = useState(TECNICA_PADRAO);
    // Giro da marcação, em graus. A prévia usa o mesmo valor que o backend
    // aplica, então o que se vê é o que sai.
    const [rotation, setRotation] = useState(0);
    // Perspectiva: os quatro cantos (0..1 do palco) puxados até uma superfície
    // inclinada. Nulo é o retângulo com giro. Ligada, a caixa para de ser a
    // posição e vira só o quadro em que o logo encaixa; quem diz onde ele cai
    // na foto são os cantos.
    const [cantos, setCantos] = useState(null);
    // Desligar e religar sem mexer na caixa devolve os cantos de antes: o
    // clique errado no botão não pode custar o encaixe inteiro.
    const perspectivaDesfeita = useRef(null);
    // Interação em curso na marcação (move | resize | rotate): muda o cursor
    // e mostra o valor em graus enquanto gira.
    const [interaction, setInteraction] = useState(null);
    const dragRef = useRef(null);
    const [inkColor, setInkColor] = useState(null);
    const [logoColors, setLogoColors] = useState([]);
    const [customInk, setCustomInk] = useState('#E84C4E');
    const [material, setMaterial] = useState(null);

    // A composição é do servidor e acontece a cada escolha do passo 2; o
    // palco mostra a amostra, não uma colagem.
    const [generating, setGenerating] = useState(false);
    const [resultImage, setResultImage] = useState(null);

    // Cada combinação já composta nesta sessão: voltar a uma escolha anterior
    // é instantâneo e não pede outra composição.
    const compostas = useRef(new Map());
    // Resposta fora de ordem não pode vencer o último clique.
    const sequencia = useRef(0);
    const materialLido = useRef(null);

    useEffect(() => {
        if (!show) return;
        setStep(chooseSource ? 'source' : 'posicao');
        setSelectedImageUrl(null);
        setMarkedArea(null);
        setDraftArea(null);
        setResultImage(null);
        setGenerating(false);
        setRotation(0);
        setCantos(null);
        materialLido.current = null;
        compostas.current.clear();
    }, [show, baseImageUrl, chooseSource]);

    useEffect(() => {
        if (!stageEl) return undefined;

        const medir = () => {
            const width = stageEl.clientWidth;
            const height = stageEl.clientHeight;
            setPalco((atual) => (atual?.width === width && atual?.height === height ? atual : { width, height }));
        };

        medir();
        const observer = new ResizeObserver(medir);
        observer.observe(stageEl);

        return () => observer.disconnect();
    }, [stageEl]);

    const sourceProduct =
        (sourceDetail?.id === sourceProductId ? sourceDetail : null) ??
        sourceProducts.find((item) => item.id === sourceProductId) ??
        null;

    const availableImages = chooseSource
        ? (uploadedImage
            ? [{ url: uploadedImage, isColor: false }]
            : getProductGalleryImageUrls(sourceProduct).map((url) => ({ url, isColor: false })))
        : (imageOptions ?? []);

    // Foto base efetiva: escolha manual > foto da cor selecionada > primeira
    // da galeria. Derivada no render para não sincronizar estado com props.
    const effectiveImageUrl =
        selectedImageUrl ?? (chooseSource ? null : baseImageUrl) ?? availableImages[0]?.url ?? null;

    // Imagem enviada na hora não tem URL pública para o backend buscar: vai no
    // corpo, como o logo.
    const isUploadedImage = !!effectiveImageUrl && effectiveImageUrl.startsWith('data:');

    const fittedImage = useFittedImageSize(stageBox, effectiveImageUrl);

    // A marcação é relativa à foto: trocar de foto invalida a área marcada.
    const selectBaseImage = (url) => {
        if (url === effectiveImageUrl) return;
        setSelectedImageUrl(url);
        setMarkedArea(null);
        setDraftArea(null);
        setCantos(null);
        dragStartRef.current = null;
    };

    const normalizedPointFromEvent = useCallback((event) => {
        const stage = stageRef.current;
        if (!stage) return null;
        const rect = stage.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            x: clamp01((event.clientX - rect.left) / rect.width),
            y: clamp01((event.clientY - rect.top) / rect.height)
        };
    }, []);

    const stageRect = () => {
        const rect = stageRef.current?.getBoundingClientRect();
        return rect && rect.width && rect.height ? rect : null;
    };

    const snapAngle = (deg, fine) => {
        let value = deg;
        while (value > 180) value -= 360;
        while (value <= -180) value += 360;
        if (fine) return Math.round(value / 15) * 15;
        for (const target of [0, 90, -90, 180]) {
            if (Math.abs(value - target) < 3) return target === 180 ? 180 : target;
        }
        return Math.round(value);
    };

    const resetTransform = () => {
        setRotation(0);
        setCantos(null);
    };

    const ligarPerspectiva = () => {
        const rect = stageRect();
        if (!rect || !markedArea) return;

        const desfeita = perspectivaDesfeita.current;

        if (desfeita?.area === markedArea && desfeita.rotation === rotation) {
            setCantos(desfeita.cantos);
            return;
        }

        setCantos(
            cantosDaCaixa(areaToPx(markedArea, rect), rotation).map(([x, y]) => [x / rect.width, y / rect.height])
        );
    };

    // O retângulo volta onde o quadrilátero está, não onde a caixa ficou
    // parada enquanto os cantos andavam.
    const desligarPerspectiva = () => {
        const rect = stageRect();

        if (rect && cantos && markedArea) {
            const area = boxToArea({
                ...areaToPx(markedArea, rect),
                cx: (cantos.reduce((soma, [x]) => soma + x, 0) / 4) * rect.width,
                cy: (cantos.reduce((soma, [, y]) => soma + y, 0) / 4) * rect.height
            }, rect);

            perspectivaDesfeita.current = { area, rotation, cantos };
            setMarkedArea(area);
        }

        setCantos(null);
    };

    // Arrasta o quadrilátero inteiro sem deixar nenhum canto sair da foto.
    const moverCantos = (origem, dx, dy, rect) => {
        const xs = origem.map(([x]) => x * rect.width);
        const ys = origem.map(([, y]) => y * rect.height);
        const mx = Math.min(Math.max(dx, -Math.min(...xs)), rect.width - Math.max(...xs));
        const my = Math.min(Math.max(dy, -Math.min(...ys)), rect.height - Math.max(...ys));

        return origem.map(([x, y]) => [x + mx / rect.width, y + my / rect.height]);
    };

    const clearMarking = () => {
        setMarkedArea(null);
        resetTransform();
    };

    // Toda interação na marcação começa aqui: captura o ponteiro e impede o
    // palco de começar a desenhar outra área por baixo.
    const beginInteraction = (event, mode, extra = {}) => {
        if (generating || !markedArea) return;
        const rect = stageRect();
        if (!rect) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragRef.current = {
            mode,
            startX: event.clientX,
            startY: event.clientY,
            startBox: areaToPx(markedArea, rect),
            startCantos: cantos,
            rect,
            ...extra
        };
        setInteraction(mode);
    };

    const endInteraction = (event) => {
        if (!dragRef.current) return;
        event.stopPropagation();
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        dragRef.current = null;
        setInteraction(null);
        stageRef.current?.focus();
    };

    const handleSelectionMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const { rect, startBox } = drag;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;

        if (drag.mode === 'move' && drag.startCantos) {
            setCantos(moverCantos(drag.startCantos, dx, dy, rect));
            return;
        }

        // Canto que cruzaria o vizinho fica onde estava: o pino para em vez
        // de virar a arte do avesso.
        if (drag.mode === 'corner') {
            const proximos = drag.startCantos.map((canto, indice) => (indice === drag.indice
                ? [clamp01(canto[0] + dx / rect.width), clamp01(canto[1] + dy / rect.height)]
                : canto));

            if (convexo(proximos.map(([x, y]) => [x * rect.width, y * rect.height]))) setCantos(proximos);
            return;
        }

        if (drag.mode === 'move') {
            setMarkedArea(boxToArea({ ...startBox, cx: startBox.cx + dx, cy: startBox.cy + dy }, rect));
            return;
        }

        if (drag.mode === 'rotate') {
            const deg = Math.atan2(event.clientY - (rect.top + startBox.cy), event.clientX - (rect.left + startBox.cx)) * 180 / Math.PI + 90;
            setRotation(snapAngle(deg, event.shiftKey));
            return;
        }

        if (drag.mode === 'resize') {
            // O arrasto acontece na tela, mas a caixa pode estar girada: leva o
            // deslocamento para o referencial da caixa, redimensiona com a
            // borda oposta fixa e devolve o centro ao referencial da tela.
            const rad = rotation * Math.PI / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);
            const lx = dx * cos + dy * sin;
            const ly = -dx * sin + dy * cos;
            const sx = drag.dir.includes('e') ? 1 : drag.dir.includes('w') ? -1 : 0;
            const sy = drag.dir.includes('s') ? 1 : drag.dir.includes('n') ? -1 : 0;
            const minW = MIN_AREA_SIDE * rect.width;
            const minH = MIN_AREA_SIDE * rect.height;
            const w = sx ? Math.max(minW, startBox.w + sx * lx) : startBox.w;
            const h = sy ? Math.max(minH, startBox.h + sy * ly) : startBox.h;
            const shiftX = sx * (w - startBox.w) / 2;
            const shiftY = sy * (h - startBox.h) / 2;
            setMarkedArea(boxToArea({
                w,
                h,
                cx: startBox.cx + shiftX * cos - shiftY * sin,
                cy: startBox.cy + shiftX * sin + shiftY * cos
            }, rect));
        }
    };

    // Teclado: setas movem (Shift = 10px), Delete apaga a marcação.
    const handleStageKeyDown = (event) => {
        if (!markedArea || generating) return;
        const rect = stageRect();
        if (!rect) return;
        const step = event.shiftKey ? 10 : 1;
        const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        if (moves[event.key] && cantos) {
            event.preventDefault();
            setCantos(moverCantos(cantos, moves[event.key][0], moves[event.key][1], rect));
        } else if (moves[event.key]) {
            event.preventDefault();
            const box = areaToPx(markedArea, rect);
            setMarkedArea(boxToArea({ ...box, cx: box.cx + moves[event.key][0], cy: box.cy + moves[event.key][1] }, rect));
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            clearMarking();
        }
    };


    const handleStagePointerDown = useCallback((event) => {
        if (generating) return;
        const point = normalizedPointFromEvent(event);
        if (!point) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragStartRef.current = point;
        setDraftArea(rectFromPoints(point, point));
    }, [generating, normalizedPointFromEvent]);

    const handleStagePointerMove = useCallback((event) => {
        if (!dragStartRef.current) return;
        const point = normalizedPointFromEvent(event);
        if (!point) return;
        setDraftArea(rectFromPoints(dragStartRef.current, point));
    }, [normalizedPointFromEvent]);

    const handleStagePointerUp = useCallback((event) => {
        if (!dragStartRef.current) return;
        const point = normalizedPointFromEvent(event);
        const area = point ? rectFromPoints(dragStartRef.current, point) : null;
        dragStartRef.current = null;
        setDraftArea(null);
        if (area && area.width >= MIN_AREA_SIDE && area.height >= MIN_AREA_SIDE) {
            // Marcação nova começa reta: giro é decisão sobre ESTA caixa. A
            // exceção é a faixa estreita e alta, que já nasce deitada — quem
            // quiser outra coisa endireita na alça de giro.
            const rect = stageRef.current?.getBoundingClientRect();
            const vertical = !!rect?.width && !!rect?.height
                && area.height * rect.height >= VERTICAL_RATIO * (area.width * rect.width);

            setMarkedArea(vertical ? swapSides(area, rect) : area);
            setRotation(vertical ? -90 : 0);
            setCantos(null);
            event.currentTarget.focus?.();
        }
    }, [normalizedPointFromEvent]);

    const onLogoDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;
        setLogoFileName(file.name);
        setLogotypeId(null);
        if (file.type === 'image/svg+xml') {
            rasterizeSvgToPng(file)
                .then((png) => setLogoBase64(png))
                .catch(() => {
                    setLogoFileName('');
                    toast.error('Não foi possível ler o SVG. Tente exportar o logo em PNG.');
                });
            return;
        }
        getBase64(file, (res) => setLogoBase64(res));
    }, []);

    // A busca do select devolve o produto resumido, sem `images`: a galeria
    // que o vendedor precisa escolher só vem no detalhe.
    useEffect(() => {
        if (!chooseSource || !sourceProductId) {
            setSourceDetail(null);
            return;
        }

        let current = true;
        api.get(`/product/${sourceProductId}`)
            .then((res) => {
                if (current && res.data?.resource?.id) setSourceDetail(res.data.resource);
            })
            .catch(renderError);

        return () => {
            current = false;
        };
    }, [chooseSource, sourceProductId]);

    // Trocar a foto de origem invalida o que foi marcado sobre a anterior.
    const resetSelection = useCallback(() => {
        setSelectedImageUrl(null);
        setMarkedArea(null);
        setDraftArea(null);
        setRotation(0);
        setCantos(null);
    }, []);

    const onSourceDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setUploadedName(file.name);
        getBase64(file, (res) => {
            setUploadedImage(res);
            setSourceProductId(null);
            resetSelection();
        });
    }, [resetSelection]);

    const {
        getRootProps: getSourceRootProps,
        getInputProps: getSourceInputProps,
        isDragActive: isSourceDragActive
    } = useDropzone({
        onDropAccepted: onSourceDrop,
        onDropRejected: () => toast.error('Formato inválido. Use JPEG, PNG ou WEBP.'),
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp']
        },
        multiple: false
    });

    const {
        getRootProps: getLogoRootProps,
        getInputProps: getLogoInputProps,
        isDragActive: isLogoDragActive
    } = useDropzone({
        onDropAccepted: onLogoDrop,
        onDropRejected: () => {
            toast.error('Formato inválido. Use JPEG, PNG, SVG ou WEBP.');
        },
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/svg+xml': ['.svg'],
            'image/webp': ['.webp']
        },
        maxFiles: 1,
        noKeyboard: true,
        // File System Access API (default no v14) monta o filtro do diálogo
        // errado no Chrome (só oferece o primeiro tipo); o input clássico
        // respeita o accept completo.
        useFsAccessApi: false
    });

    // Sem empresa nem pessoa no orçamento não há biblioteca onde guardar: o
    // anexo continua avulso, só nesse caso.
    const temCliente = !!(companyId || customerId);

    const removeLogo = () => {
        setLogoBase64(null);
        setLogoFileName('');
        setLogotypeId(null);
    };

    useEffect(() => {
        if (!logoBase64) {
            setLogoColors([]);
            return;
        }

        let current = true;
        extractLogoColors(logoBase64).then((colors) => {
            if (!current) return;

            setLogoColors(colors);
            // Trocar o logo invalida a cor sugerida pelo anterior; branco,
            // preto e a cor escolhida à mão continuam valendo.
            setInkColor((chosen) =>
                chosen === null ||
                chosen === customInk ||
                CORES_TINTA.some((item) => item.value === chosen) ||
                colors.includes(chosen)
                    ? chosen
                    : null
            );
        });

        return () => {
            current = false;
        };
    }, [logoBase64, customInk]);

    // Enquanto o fundo não sai, a marcação fica sem arte: mostrar o original
    // seria exibir o branco para trocá-lo logo depois.
    useEffect(() => {
        if (!logoBase64) return;

        let current = true;
        removeLogoBackground(logoBase64).then((url) => {
            if (current) setLogoPreview({ source: logoBase64, url });
        });

        return () => {
            current = false;
        };
    }, [logoBase64]);

    const logoArt = logoPreview?.source === logoBase64 ? logoPreview.url : null;

    // O que o passo da gravação exige para compor.
    const podeAvancar = !!effectiveImageUrl && !!markedArea && !!logoBase64;

    // Botão desabilitado sem explicação faz o vendedor procurar defeito na
    // tela: o que falta fica escrito no rodapé.
    const blockReason = podeAvancar
        ? (step === 'gravacao'
            ? 'Imagem ilustrativa. A personalização pode variar.'
            : cantos
                ? 'Arraste cada canto até a borda da superfície do produto.'
                : 'Arraste a marcação sobre a foto para escolher onde a arte vai.')
        : !effectiveImageUrl
            ? 'Escolha a foto do produto.'
            : !markedArea
                ? 'Marque a área de gravação sobre a foto.'
                : 'Anexe o logo que será aplicado.';

    const previa = step === 'gravacao';
    const semTinta = SEM_TINTA.includes(technique);
    const semBranco = SEM_BRANCO.includes(technique);

    // Trocar para uma técnica que não tem a cor escolhida deixaria a gravação
    // invisível — na sublimação o branco não muda um pixel.
    useEffect(() => {
        if (corImpossivel(technique, inkColor)) setInkColor(null);
    }, [technique, inkColor]);

    // O material muda o acabamento, e ninguém abre a amostra para responder de
    // que material é o produto que está na tela. A ficha costuma dizer; quando
    // não diz, fica "Outros" e o servidor deduz pela foto. O material também
    // escolhe a gravação de abertura — só na abertura, depois manda a escolha.
    useEffect(() => {
        if (!show) return;

        const nome = sourceProduct?.name ?? productName;
        const descricao = sourceProduct?.description ?? productDescription;

        if (!nome && !descricao) return;

        // Guardar o produto lido, e não um "já li": no gerador avulso o
        // vendedor troca de produto sem fechar o modal, e o material do
        // anterior continuaria marcado.
        const doProduto = sourceProduct?.id ?? productId ?? nome;

        if (materialLido.current === doProduto) return;

        materialLido.current = doProduto;

        const achado = materialPelaDescricao(nome, descricao);

        if (!achado) return;

        setMaterial(achado);
        setTechnique(tecnicaParaMaterial(achado));
    }, [show, sourceProduct, productId, productName, productDescription]);

    /**
     * Tudo que define uma amostra. É a chave do cache local e, na prática, a
     * mesma identidade que o servidor usaria para não recompor o que já existe.
     */
    const cantosEnviados = cantos && cantos.map(([x, y]) => ({ x: arredondar(clamp01(x)), y: arredondar(clamp01(y)) }));

    const chave = (step === 'gravacao' && effectiveImageUrl && markedArea && logoBase64)
        ? JSON.stringify({
            foto: isUploadedImage ? `upload:${effectiveImageUrl.length}` : effectiveImageUrl,
            logo: logotypeId ?? `${logoFileName}:${logoBase64.length}`,
            area: [markedArea.x, markedArea.y, markedArea.width, markedArea.height].map(arredondar),
            rotation: Math.round(rotation) || 0,
            cantos: cantosEnviados,
            technique,
            ink: semTinta ? null : (inkColor ?? null),
            material: material ?? null
        })
        : null;

    // O efeito abaixo depende só da CHAVE: durante o arrasto a marcação muda a
    // cada pixel e volta a valores já arredondados o tempo todo, e reagir à
    // identidade do objeto pediria a mesma amostra duas vezes.
    const pedidoRef = useRef(null);

    pedidoRef.current = {
        budget_id: quoteId ?? null,
        budget_item_id: itemId ?? null,
        product_id: productId ?? null,
        subproduct_id: subproductId ?? null,
        base_image_url: isUploadedImage ? null : effectiveImageUrl,
        base_image: isUploadedImage ? effectiveImageUrl : null,
        area: markedArea && {
            x: arredondar(markedArea.x),
            y: arredondar(markedArea.y),
            width: arredondar(markedArea.width),
            height: arredondar(markedArea.height)
        },
        logo: logoBase64,
        technique: technique ?? null,
        rotation: Math.round(rotation) || null,
        corners: cantosEnviados,
        ink_color: semTinta ? null : inkColor,
        material: material
    };

    // O palco é a amostra de verdade, não uma colagem: mudar técnica, cor,
    // material ou marcação recompõe. No passo da posição nada disso roda — lá
    // a prévia é do navegador, e arrastar não pode custar uma ida ao servidor.
    useEffect(() => {
        if (!chave) {
            setGenerating(false);
            return undefined;
        }

        const pronta = compostas.current.get(chave);

        if (pronta) {
            sequencia.current += 1;
            setGenerating(false);
            setResultImage(pronta);
            return undefined;
        }

        const id = (sequencia.current += 1);

        setGenerating(true);

        const timer = setTimeout(() => {
            api.post(`budget/product-mock/generate`, pedidoRef.current)
                .then((res) => {
                    const image = res.data?.resource?.image ?? res.data?.resource?.image_base64 ?? null;

                    if (!image) {
                        if (id === sequencia.current) {
                            setGenerating(false);
                            toast.error('A composição não retornou uma imagem. Tente novamente.');
                        }
                        return;
                    }

                    compostas.current.set(chave, image);

                    if (id !== sequencia.current) return;

                    setResultImage(image);
                    setGenerating(false);
                })
                .catch((err) => {
                    if (id !== sequencia.current) return;

                    setGenerating(false);
                    renderError(err);
                });
        }, ATRASO);

        return () => clearTimeout(timer);
    }, [chave]);

    // No gerador avulso não há item de orçamento onde gravar: a amostra sai
    // como arquivo.
    const downloadResult = () => {
        if (!resultImage) return;

        const link = document.createElement('a');
        link.href = resultImage;
        link.download = `mockup-${(sourceProduct?.name || uploadedName || 'produto')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '')}.webp`;
        link.click();
    };

    const applyResult = () => {
        if (!resultImage) return;
        onApply(resultImage, {
            product_id: productId ?? null,
            subproduct_id: subproductId ?? null,
            customer_logotype_id: logotypeId ?? null,
            area: pedidoRef.current.area,
            technique: technique ?? null,
            ink_color: semTinta ? null : (inkColor ?? null),
            material: material ?? null,
            rotation: Math.round(rotation) || 0,
            corners: cantosEnviados,
            logotype: { name: logoFileName, image_url: logoBase64 }
        });
    };

    const arte = logoBase64 ? (logoArt && (
        <span className="generate-mockup__area-art">
            <img
                className="generate-mockup__area-logo"
                src={logoArt}
                alt=""
                draggable={false}
                style={inkColor ? { visibility: 'hidden' } : undefined}
            />
            {inkColor && (
                <span
                    className="generate-mockup__area-tint"
                    style={{
                        backgroundColor: inkColor,
                        WebkitMaskImage: `url(${logoArt})`,
                        maskImage: `url(${logoArt})`
                    }}
                />
            )}
        </span>
    )) : (
        <span className="generate-mockup__area-label">Logo aqui</span>
    );

    const cantosPx = cantos && palco ? cantos.map(([x, y]) => [x * palco.width, y * palco.height]) : null;
    const quadro = cantosPx && markedArea ? areaToPx(markedArea, palco) : null;

    // A ferramenta solta fica acima do quadrilátero; colado no topo da foto,
    // vai para baixo, senão o palco corta.
    const ferramentaSolta = cantosPx && (() => {
        const xs = cantosPx.map(([x]) => x);
        const ys = cantosPx.map(([, y]) => y);
        const topo = Math.min(...ys);

        return {
            left: (Math.min(...xs) + Math.max(...xs)) / 2,
            top: topo >= 44 ? topo - 40 : Math.max(...ys) + 12
        };
    })();

    const irPara = (destino) => {
        if (destino !== 'posicao' && !podeAvancar) {
            toast.error(blockReason);
            return;
        }

        setStep(destino);
    };

    const footer = step === 'source' ? (
        <div className="generate-mockup__footer">
            <div className="generate-mockup__footer-actions">
                <Button type={`secondary`} text={`Cancelar`} action={close} />
                <Button
                    type={`primary`}
                    text={`Continuar`}
                    action={() => setStep('posicao')}
                    disabled={!effectiveImageUrl}
                />
            </div>
        </div>
    ) : step === 'posicao' ? (
        <div className="generate-mockup__footer">
            <span className="generate-mockup__footer-hint">{blockReason}</span>
            <div className="generate-mockup__footer-actions">
                <Button
                    type={`secondary`}
                    text={chooseSource ? 'Voltar' : 'Cancelar'}
                    action={chooseSource ? () => setStep('source') : close}
                />
                <Button
                    type={`primary`}
                    text={`Escolher a gravação`}
                    action={() => irPara('gravacao')}
                    disabled={!podeAvancar}
                />
            </div>
        </div>
    ) : (
        <div className="generate-mockup__footer">
            <span className="generate-mockup__footer-hint">{blockReason}</span>
            <div className="generate-mockup__footer-actions">
                <Button type={`secondary`} text={`Voltar`} action={() => setStep('posicao')} />
                {chooseSource ? (
                    <Button
                        type={`primary`}
                        svg={`download`}
                        text={`Baixar imagem`}
                        action={downloadResult}
                        disabled={!resultImage}
                    />
                ) : (
                    <>
                        <Button
                            type={`secondary`}
                            svg={`download`}
                            text={`Baixar`}
                            action={downloadResult}
                            disabled={!resultImage}
                        />
                        <Button
                            type={`primary`}
                            svg={`check`}
                            text={`Usar esta amostra`}
                            action={applyResult}
                            disabled={!resultImage}
                        />
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Modal
            className={`modal--generate-mockup`}
            title={
                <span className="generate-mockup__head">
                    <span className="generate-mockup__head-text">
                        <strong>{chooseSource ? 'Gerador de mockup' : 'Simule com o logo do cliente'}</strong>
                        <small>{sourceProduct?.name || productName || uploadedName}</small>
                    </span>

                    {step !== 'source' && (
                        <span className="generate-mockup__steps">
                            {PASSOS.map((item, indice) => (
                                <React.Fragment key={item.id}>
                                    {indice > 0 && <Icon name="arrow" size={14} />}
                                    <button
                                        type="button"
                                        className={`generate-mockup__step${item.id === step ? ' generate-mockup__step--active' : ''}`}
                                        onClick={() => irPara(item.id)}
                                        aria-current={item.id === step ? 'step' : undefined}
                                    >
                                        <i>{PASSOS.findIndex((passo) => passo.id === step) > indice
                                            ? <Icon name="check" size={11} />
                                            : indice + 1}</i>
                                        <span>{item.rotulo}</span>
                                    </button>
                                </React.Fragment>
                            ))}
                        </span>
                    )}
                </span>
            }
            show={show}
            close={close}
            size={`xlarge`}
            footer={footer}
        >
            {step === 'source' && (
                <div className="generate-mockup__source">
                    <div className="generate-mockup__source-picker">
                        <h4 className="generate-mockup__step-title">1. Qual é a foto do produto?</h4>
                        <p className="generate-mockup__hint">
                            Escolha um produto do catálogo ou envie uma imagem do seu computador.
                        </p>

                        <div
                            className={`generate-mockup__source-card${
                                sourceProductId && !uploadedImage ? ' generate-mockup__source-card--active' : ''
                            }`}
                        >
                            <span className="generate-mockup__source-card-title">
                                <Icon name="product" size={16} />
                                Produto do catálogo
                            </span>
                            <SelectProduct
                                selectedProduct={sourceProductId}
                                setSelectedProduct={(id) => {
                                    setSourceProductId(id);
                                    setUploadedImage(null);
                                    setUploadedName('');
                                    resetSelection();
                                }}
                                changeProducts={setSourceProducts}
                            />
                        </div>

                        <span className="generate-mockup__source-or">ou</span>

                        <div
                            className={`generate-mockup__source-card${
                                uploadedImage ? ' generate-mockup__source-card--active' : ''
                            }`}
                        >
                            <span className="generate-mockup__source-card-title">
                                <Icon name="upload" size={16} />
                                Enviar uma imagem
                            </span>

                            {uploadedImage ? (
                                <div className="generate-mockup__logo-preview">
                                    <img src={uploadedImage} alt="" />
                                    <span className="generate-mockup__logo-name" title={uploadedName}>
                                        {uploadedName}
                                    </span>
                                    <button
                                        type="button"
                                        className="generate-mockup__logo-remove"
                                        aria-label="Remover imagem"
                                        onClick={() => {
                                            setUploadedImage(null);
                                            setUploadedName('');
                                            resetSelection();
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <div
                                    {...getSourceRootProps({
                                        className: [
                                            'generate-mockup__logo-drop',
                                            isSourceDragActive ? 'generate-mockup__logo-drop--active' : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ')
                                    })}
                                >
                                    <input {...getSourceInputProps()} />
                                    <Icon name="upload" size={18} />
                                    <span>Clique ou arraste a foto do produto</span>
                                    <small>JPEG, PNG ou WEBP</small>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="generate-mockup__source-stage">
                        {effectiveImageUrl ? (
                            <>
                                <img src={effectiveImageUrl} alt="" />
                                {availableImages.length > 1 && (
                                    <div className="generate-mockup__thumbs">
                                        {availableImages.map((option) => (
                                            <button
                                                key={option.url}
                                                type="button"
                                                className={`generate-mockup__thumb${
                                                    option.url === effectiveImageUrl
                                                        ? ' generate-mockup__thumb--selected'
                                                        : ''
                                                }`}
                                                onClick={() => setSelectedImageUrl(option.url)}
                                                title={`Usar esta foto`}
                                            >
                                                <img src={option.url} alt="" draggable={false} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <span className="generate-mockup__source-caption">
                                    {sourceProduct?.name || uploadedName}
                                    {availableImages.length > 1 &&
                                        ` · ${availableImages.length} fotos`}
                                </span>
                            </>
                        ) : (
                            <div className="generate-mockup__source-empty">
                                <Icon name="image" size={28} />
                                <span>A foto escolhida aparece aqui</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {(step === 'posicao' || step === 'gravacao') && (
                <div className="generate-mockup">
                    <div className="generate-mockup__canvas-col">
                        <div className="generate-mockup__stage-outer" ref={setStageBox}>
                            {!effectiveImageUrl ? (
                                <p className="generate-mockup__stage-empty">
                                    O produto selecionado não tem foto disponível para gerar a amostra.
                                </p>
                            ) : step === 'gravacao' && resultImage ? (
                                <div className="generate-mockup__stage-wrap">
                                    <figure className="generate-mockup__result-frame">
                                        <img src={resultImage} alt="" style={fittedImage ?? undefined} />
                                    </figure>
                                    {generating && (
                                        <span className="generate-mockup__scan" aria-hidden><i /></span>
                                    )}
                                </div>
                            ) : (
                                /* No passo da gravação, enquanto a primeira
                                   composição não chega, fica a mesma marcação
                                   do passo anterior — só de olhar. Sem isso o
                                   logo sumiria da tela para voltar depois. */
                                <div className="generate-mockup__stage-wrap">
                                <div
                                    ref={ligarPalco}
                                    tabIndex={previa ? -1 : 0}
                                    className={`generate-mockup__stage${markedArea || draftArea ? ' generate-mockup__stage--marked' : ''}${interaction ? ` generate-mockup__stage--${interaction}` : ''}${previa ? ' generate-mockup__stage--previa' : ''}`}
                                    onPointerDown={previa ? undefined : handleStagePointerDown}
                                    onPointerMove={previa ? undefined : handleStagePointerMove}
                                    onPointerUp={previa ? undefined : handleStagePointerUp}
                                    onPointerCancel={previa ? undefined : handleStagePointerUp}
                                    onKeyDown={previa ? undefined : handleStageKeyDown}
                                >
                                    <img
                                        key={effectiveImageUrl}
                                        src={effectiveImageUrl}
                                        alt=""
                                        draggable={false}
                                        style={fittedImage ?? undefined}
                                    />
                                    {draftArea && (
                                        <div
                                            className="generate-mockup__area generate-mockup__area--draft"
                                            style={{
                                                left: `${draftArea.x * 100}%`,
                                                top: `${draftArea.y * 100}%`,
                                                width: `${draftArea.width * 100}%`,
                                                height: `${draftArea.height * 100}%`
                                            }}
                                        />
                                    )}
                                    {markedArea && !draftArea && !cantos && (
                                        <div
                                            className={`generate-mockup__area generate-mockup__area--selected${interaction ? ' is-dragging' : ''}${previa ? ' generate-mockup__area--previa' : ''}`}
                                            style={{
                                                left: `${markedArea.x * 100}%`,
                                                top: `${markedArea.y * 100}%`,
                                                width: `${markedArea.width * 100}%`,
                                                height: `${markedArea.height * 100}%`,
                                                transform: `rotate(${rotation}deg)`
                                            }}
                                            onPointerDown={previa ? undefined : (e) => beginInteraction(e, 'move')}
                                            onPointerMove={previa ? undefined : handleSelectionMove}
                                            onPointerUp={previa ? undefined : endInteraction}
                                            onPointerCancel={previa ? undefined : endInteraction}
                                        >
                                            {arte}

                                            {!previa && ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((dir) => (
                                                <span
                                                    key={dir}
                                                    className={`generate-mockup__resize generate-mockup__resize--${dir}`}
                                                    onPointerDown={(e) => beginInteraction(e, 'resize', { dir })}
                                                    onPointerMove={handleSelectionMove}
                                                    onPointerUp={endInteraction}
                                                    onPointerCancel={endInteraction}
                                                />
                                            ))}

                                            {!previa && <span className="generate-mockup__rotate-stem" />}
                                            {!previa && (
                                                <span className="generate-mockup__ferramentas">
                                                    <button
                                                        type="button"
                                                        className="generate-mockup__ferramenta generate-mockup__rotate"
                                                        title="Arraste para girar · Shift trava em 15° · duplo clique endireita"
                                                        aria-label="Girar marcação"
                                                        onPointerDown={(e) => beginInteraction(e, 'rotate')}
                                                        onPointerMove={handleSelectionMove}
                                                        onPointerUp={endInteraction}
                                                        onPointerCancel={endInteraction}
                                                        onDoubleClick={(e) => { e.stopPropagation(); setRotation(0); }}
                                                    >
                                                        <Icon name="refresh" size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="generate-mockup__ferramenta"
                                                        title="Perspectiva: puxe os cantos até a superfície inclinada"
                                                        aria-label="Ligar perspectiva"
                                                        aria-pressed="false"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => { e.stopPropagation(); ligarPerspectiva(); }}
                                                    >
                                                        <IconePerspectiva />
                                                    </button>
                                                </span>
                                            )}

                                            {interaction === 'rotate' && (
                                                <span className="generate-mockup__angle" style={{ transform: `rotate(${-rotation}deg)` }}>
                                                    {rotation > 0 ? '+' : ''}{rotation}°
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {markedArea && !draftArea && cantosPx && (
                                        <div className={`generate-mockup__perspectiva${interaction ? ' is-dragging' : ''}`}>
                                            <div
                                                className="generate-mockup__perspectiva-arte"
                                                style={{
                                                    width: quadro.w,
                                                    height: quadro.h,
                                                    transform: matrizCss(quadro.w, quadro.h, cantosPx)
                                                }}
                                            >
                                                {arte}
                                            </div>

                                            {!previa && (
                                                <>
                                                    <svg
                                                        className="generate-mockup__perspectiva-contorno"
                                                        viewBox={`0 0 ${palco.width} ${palco.height}`}
                                                        aria-hidden
                                                    >
                                                        <polygon
                                                            points={cantosPx.map((ponto) => ponto.join(',')).join(' ')}
                                                            onPointerDown={(e) => beginInteraction(e, 'move')}
                                                            onPointerMove={handleSelectionMove}
                                                            onPointerUp={endInteraction}
                                                            onPointerCancel={endInteraction}
                                                        />
                                                    </svg>

                                                    {cantosPx.map(([x, y], indice) => (
                                                        <span
                                                            key={indice}
                                                            className="generate-mockup__canto"
                                                            style={{ left: x, top: y }}
                                                            onPointerDown={(e) => beginInteraction(e, 'corner', { indice })}
                                                            onPointerMove={handleSelectionMove}
                                                            onPointerUp={endInteraction}
                                                            onPointerCancel={endInteraction}
                                                        />
                                                    ))}

                                                    <span className="generate-mockup__ferramentas" style={ferramentaSolta}>
                                                        <button
                                                            type="button"
                                                            className="generate-mockup__ferramenta generate-mockup__ferramenta--ativa"
                                                            title="Voltar ao retângulo"
                                                            aria-label="Desligar perspectiva"
                                                            aria-pressed="true"
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => { e.stopPropagation(); desligarPerspectiva(); }}
                                                        >
                                                            <IconePerspectiva />
                                                        </button>
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                    {generating && (
                                        <span className="generate-mockup__scan" aria-hidden><i /></span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="generate-mockup__form-col">
                        {step === 'posicao' && (
                        <>
                        {availableImages.length > 1 && (
                            <>
                            <h4 className="generate-mockup__step-title">Foto do produto</h4>
                            <div className="generate-mockup__thumbs generate-mockup__thumbs--grade">
                                {availableImages.map((option) => (
                                    <button
                                        key={option.url}
                                        type="button"
                                        className={[
                                            'generate-mockup__thumb',
                                            option.url === effectiveImageUrl ? 'generate-mockup__thumb--selected' : '',
                                            option.isColor ? 'generate-mockup__thumb--color' : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => selectBaseImage(option.url)}
                                        title={option.isColor ? 'Foto da cor selecionada' : 'Usar esta foto'}
                                    >
                                        <img src={option.url} alt="" draggable={false} />
                                        {option.isColor && (
                                            <span className="generate-mockup__thumb-badge">Cor</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            </>
                        )}

                        <h4 className="generate-mockup__step-title">Anexe o logo</h4>

                        {temCliente ? (
                            <CustomerLogotypes
                                companyId={companyId}
                                customerId={customerId}
                                compact
                                selectedId={logotypeId}
                                onSelect={(dataUri, logotype) => {
                                    setLogoBase64(dataUri);
                                    setLogoFileName(logotype.name);
                                    setLogotypeId(logotype.id);
                                }}
                                onRemoved={removeLogo}
                            />
                        ) : !logoBase64 ? (
                            <div
                                {...getLogoRootProps({
                                    className: [
                                        'generate-mockup__logo-drop',
                                        isLogoDragActive ? 'generate-mockup__logo-drop--active' : ''
                                    ]
                                        .filter(Boolean)
                                        .join(' ')
                                })}
                            >
                                <input {...getLogoInputProps()} />
                                <Icon name="upload" size={16} />
                                <span>
                                    Clique ou arraste o logo aqui
                                    <small>JPEG, PNG, SVG ou WEBP — de preferência com fundo transparente</small>
                                </span>
                            </div>
                        ) : (
                            <div className="generate-mockup__logo-preview">
                                <img src={logoBase64} alt="" />
                                <span className="generate-mockup__logo-name" title={logoFileName}>
                                    {logoFileName}
                                </span>
                                <button
                                    type="button"
                                    className="generate-mockup__logo-remove"
                                    aria-label="Remover logo"
                                    onClick={removeLogo}
                                >
                                    ×
                                </button>
                            </div>
                        )}

                        <p className="generate-mockup__notice">
                            <Icon name="sparkles" size={16} />
                            <span>
                                <strong>Isto é só o posicionamento.</strong>
                                No próximo passo você escolhe o tipo de gravação e a cor — é lá que a arte
                                ganha o acabamento real.
                            </span>
                        </p>
                        </>
                        )}

                        {step === 'gravacao' && (
                        <>
                        <div className="generate-mockup__field">
                            <span className="generate-mockup__field-label">Material do produto</span>
                            <div className="generate-mockup__techniques">
                                {MATERIAIS.map((item) => (
                                    <button
                                        key={item.label}
                                        type="button"
                                        className={`generate-mockup__technique${material === item.value ? ' generate-mockup__technique--selected' : ''}`}
                                        onClick={() => setMaterial(item.value)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {FAMILIAS.map((familia) => {
                            const daFamilia = TECNICAS.filter((item) => item.familia === familia.id);

                            if (!daFamilia.length) return null;

                            return (
                                <div className="generate-mockup__field" key={familia.id}>
                                    <span className="generate-mockup__field-label">{familia.titulo}</span>
                                    <div className="generate-mockup__cards">
                                        {daFamilia.map((item) => (
                                            <button
                                                key={item.value}
                                                type="button"
                                                className={`generate-mockup__card${technique === item.value ? ' generate-mockup__card--selected' : ''}`}
                                                onClick={() => setTechnique(item.value)}
                                                aria-pressed={technique === item.value}
                                            >
                                                <img src={item.demo} alt={`Exemplo de ${item.label}`} loading="lazy" />
                                                <span className="generate-mockup__card-text">
                                                    <strong>{item.label}</strong>
                                                    <small>{item.resumo}</small>
                                                    <em>{item.onde}</em>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="generate-mockup__field">
                            <span className="generate-mockup__field-label">Cor da gravação</span>
                            {semTinta ? (
                                <p className="generate-mockup__field-hint">
                                    Esse acabamento não usa tinta: o tom sai do material do produto.
                                </p>
                            ) : (
                                <>
                                    <div className="generate-mockup__techniques">
                                        {CORES_TINTA.filter((item) => !corImpossivel(technique, item.value)).map((item) => (
                                            <button
                                                key={item.label}
                                                type="button"
                                                className={`generate-mockup__technique${inkColor === item.value ? ' generate-mockup__technique--selected' : ''}`}
                                                onClick={() => setInkColor(item.value)}
                                            >
                                                {item.value && (
                                                    <span
                                                        className="generate-mockup__swatch"
                                                        style={{ backgroundColor: item.value }}
                                                    />
                                                )}
                                                {item.label}
                                            </button>
                                        ))}
                                        {logoColors.filter((color) => !corImpossivel(technique, color)).map((color) => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`generate-mockup__technique${inkColor === color ? ' generate-mockup__technique--selected' : ''}`}
                                                onClick={() => setInkColor(color)}
                                            >
                                                <span
                                                    className="generate-mockup__swatch"
                                                    style={{ backgroundColor: color }}
                                                />
                                                {color}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            className={`generate-mockup__technique${inkColor === customInk ? ' generate-mockup__technique--selected' : ''}`}
                                            onClick={() => setInkColor(customInk)}
                                        >
                                            <span
                                                className="generate-mockup__swatch"
                                                style={{ backgroundColor: customInk }}
                                            />
                                            Outra
                                        </button>
                                    </div>
                                    {inkColor === customInk && (
                                        <InputColor
                                            value={customInk}
                                            change={(value) => {
                                                if (corImpossivel(technique, value)) {
                                                    toast.info('Sublimação não imprime branco: o claro da arte é o próprio tecido.');
                                                    return;
                                                }

                                                setCustomInk(value);
                                                setInkColor(value);
                                            }}
                                        />
                                    )}
                                </>
                            )}

                            {semBranco && (
                                <p className="generate-mockup__field-hint">
                                    Sublimação não imprime branco: o claro da arte é o próprio tecido.
                                </p>
                            )}
                        </div>
                        </>
                        )}
                    </div>
                </div>
            )}

        </Modal>
    );
};

export default ModalGenerateMockup;
